import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ChevronUpIcon, MapLayerIcon } from '@rsos/assets/icons';
import { Box } from '@rsos/base-components';
import { PURE_WIRELESS_CAPABILITY } from '@rsos/capstone/src/constants/capabilities';
import { IRP_MAP_ENABLED } from '@rsos/capstone/src/constants/permissionNames';
import BasemapTooltip from '@rsos/components/capstone/BasemapMenu/BasemapTooltip';
import Popover from '@rsos/components/capstone/base/Popover';
import {
  allSpatialLayersSelector,
  filteredDynamicLayers,
} from '@rsos/components/capstone/common/Map/SpatialLayers/selectors';
import { CONNECTED_SITES_CATEGORY } from '@rsos/constants/dataTypeCategories';
import { useFlag } from '@rsos/flags';
import useHasCapability from '@rsos/hooks/useHasCapability';
import { GOOGLE } from '@rsos/maps/constants/mapProviders';
import {
  clearAllGISServices,
  selectActiveNonBasemapServices,
  selectGISLayers,
} from '@rsos/reduxDataStore';
import {
  selectAdditionalLayersCount,
  clearAllAdditionalLayers,
} from '@rsos/reduxDataStore/src/features/additionalLayers/additionalLayersSlice';
import { selectUserPermissionsByApp } from '@rsos/sinatra';
import { useAppDispatch, useAppSelector } from '@rsos/utils/reduxHooks';
import { clearCheckedLayers } from '../../actions/spatialLayers';
import { useEsriAccess } from '../../hooks/useEsriAccess';
import { AdditionalLayersControls } from './AdditionalLayersControls/AdditionalLayersControls';
import { GISLayerControls } from './GISLayerControls/GISLayerControls';
import LayerCheckbox from './LayerCheckbox';
import { RegularCheckboxWrapper } from './LayerCheckbox.styles';
import LayerCheckboxGroup from './LayerCheckboxGroup';
import {
  Controls,
  AccordionTitleWrapper,
  AccordionTitle,
  CollapsedIconWrapper,
  AccordionContent,
  ControlsBottom,
  SelectedLayersWrapper,
  SelectedText,
  ClearAllText,
} from './MapLayersControl.styles';
import { getGroupedlayers } from './helpers';

/**
 * MapLayersControl without category tabs.
 * Shows all layers in a single flat list.
 *
 * Delete this file once the layer_category capability has been
 * rolled out to all PSAPs and MapLayersControlWithTabs is the
 * only version in use.
 */
const MapLayersControl = () => {
  const dispatch = useAppDispatch();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isGmrLandingZoneEnabled = useFlag('gmr_landing_zone');

  const permissions = useAppSelector(state =>
    selectUserPermissionsByApp(state.sinatra.user, 'capstone'),
  );

  const hasEsriMapAccess = useEsriAccess();
  const { irp_access_token } = useAppSelector(state => state.sinatra.irp);
  const isIRPmode =
    permissions && permissions.includes(IRP_MAP_ENABLED) && irp_access_token;

  const gisServicesPresent = useSelector(selectGISLayers).length > 0;
  // Exclude the basemap service (toggled via BasemapMenu) since it is not
  // rendered in the MapLayersControl and should not count toward the
  // "Selected" layer total.
  const numActiveGISServices = useSelector(
    selectActiveNonBasemapServices,
  ).length;
  const numAdditionalLayers = useSelector(selectAdditionalLayersCount);
  const shouldRenderGISControls =
    hasEsriMapAccess && gisServicesPresent && (!irp_access_token || isIRPmode);

  const mapProvider = useAppSelector(state => state.sinatra.ui.mapProvider);

  const allSpatialLayers = useAppSelector(state =>
    allSpatialLayersSelector(state.spatialLayers),
  );
  const dynamicLayers = useAppSelector(
    state => state.spatialLayers.dynamicLayers,
  );
  const disabledDynamicLayers = useAppSelector(
    state => state.spatialLayers.disabledDynamicLayers,
  );
  const shouldDisplayDynamicLayers = !!filteredDynamicLayers(
    dynamicLayers,
    disabledDynamicLayers,
  ).length;

  const isLayerDisabled = useMemo(() => mapProvider === GOOGLE, [mapProvider]);

  const [hoveredLayerId, setHoveredLayerId] = useState(null);
  const hasPureWirelessCapability = useHasCapability(
    PURE_WIRELESS_CAPABILITY,
  ).hasCapability;

  const isPureWirelessLayerEnabled =
    useFlag('pure_wireless_layer') && hasPureWirelessCapability;

  const onHandleHoverLayer = layerID => setHoveredLayerId(layerID);

  // Check whether or not to render the component on page load or when receiving
  // dynamic spatial object data when the user doesn't have access to any static
  // spatial layers. This check is necessary because the user is able to receive
  // spatial layer data if the permission is enabled, but capability is disabled
  const shouldRenderMLControls = !irp_access_token || isIRPmode;

  const toggleAccordion = () => {
    setIsCollapsed(isCollapsed => !isCollapsed);
  };

  const handleClearAll = useCallback(() => {
    setIsCollapsed(true);
    if (hasEsriMapAccess) {
      dispatch(clearAllGISServices());
    }
    dispatch(clearCheckedLayers());
    dispatch(clearAllAdditionalLayers());
  }, [hasEsriMapAccess, dispatch]);

  const [layerCheckboxes, setLayerCheckboxes] = useState([]);
  const [groupedLayerCheckboxes, setGroupedLayerCheckboxes] = useState({});

  const selectedCheckboxCount =
    numActiveGISServices + // will be 0 if hasEsriMapAccess is false
    numAdditionalLayers + // Pure Wireless and other additional layers
    layerCheckboxes.reduce((count, cb) => {
      if (cb?.layer?.isChecked) {
        return count + 1;
      }
      return count;
    }, 0) +
    Object.values(groupedLayerCheckboxes).reduce((count, cb) => {
      if (cb?.layers?.find(i => i.layer.isChecked)) {
        return count + 1;
      }
      return count;
    }, 0);

  // This effect is used to set the layer checkboxes for the map layers control.
  // When the spatial layers change, the checkboxes are updated to reflect the
  // new state of the spatial layers. Based on this premise, the count of
  // selected checkboxes can be made by reducing the layerCheckboxes state,
  // see selectedCheckboxCount above.
  useEffect(() => {
    // Removes placeholder layers that are not meant to be toggled or seen by users.
    const filterPlaceholders = layer => {
      return (
        layer.key !== 'avive_aed_placeholder' &&
        layer.key !== 'avive_aed_highlight' &&
        layer.category !== CONNECTED_SITES_CATEGORY &&
        (layer.key !== 'gmr_landing_zones' || isGmrLandingZoneEnabled)
      );
    };

    const spatialLayers = allSpatialLayers.map(layer => ({
      id: layer.key,
      key: layer.key,
      category: layer.category?.key,
      layer,
      isGISSupplementalLayer: false,
      isDisabled: false,
    }));
    const groupedLayers = getGroupedlayers(spatialLayers);
    setGroupedLayerCheckboxes(groupedLayers);

    setLayerCheckboxes([...spatialLayers.filter(filterPlaceholders)]);
  }, [allSpatialLayers, mapProvider, isLayerDisabled, isGmrLandingZoneEnabled]);

  if (
    !(
      shouldRenderMLControls &&
      (shouldRenderGISControls ||
        shouldDisplayDynamicLayers ||
        layerCheckboxes.length > 0 ||
        Object.keys(groupedLayerCheckboxes).length > 0 ||
        isPureWirelessLayerEnabled)
    )
  ) {
    return null;
  }

  return (
    <Controls>
      <AccordionTitleWrapper onClick={toggleAccordion} horizontal>
        <MapLayerIcon color="primaryText" width={16} height={18} />
        <AccordionTitle data-name="map-layers-control-title">
          Map Layers
        </AccordionTitle>
        <CollapsedIconWrapper isCollapsed={isCollapsed}>
          <ChevronUpIcon color="secondaryLink" width={16} height={12} />
        </CollapsedIconWrapper>
      </AccordionTitleWrapper>
      {!isCollapsed && (
        <>
          <AccordionContent>
            {/* ECC-specific layers (geocomm URLs) */}
            {shouldRenderGISControls && (
              <GISLayerControls filter="ecc-specific" />
            )}

            {/* General GIS Supplemental layers (weather, traffic, etc.) */}
            {shouldRenderGISControls && (
              <GISLayerControls filter="supplemental" />
            )}

            {/* Additional Layers (Pure Wireless, etc.) */}
            {isPureWirelessLayerEnabled && <AdditionalLayersControls />}

            {/* Static / Dynamic spatial layers */}
            {layerCheckboxes.map(cb => {
              return (
                <div
                  key={cb.id}
                  onMouseEnter={() => onHandleHoverLayer(cb.id)}
                  onMouseLeave={() => onHandleHoverLayer(null)}
                >
                  <Popover
                    cssDisplayProperty="block"
                    isOpen={cb?.isDisabled ? cb.id === hoveredLayerId : false}
                    popoverID={`map-layers-control-popover-${cb.id}`}
                    trigger={
                      <Box horizontal>
                        <Box grow>
                          <LayerCheckbox
                            id={cb.layer.id}
                            isDisabled={cb.isDisabled}
                            isGISSupplementalLayer={cb.isGISSupplementalLayer}
                            key={cb.key}
                            layer={cb.layer}
                          />
                        </Box>
                      </Box>
                    }
                    useCustomOffset
                    xOffset={0}
                    yOffset={24}
                  >
                    <BasemapTooltip />
                  </Popover>
                </div>
              );
            })}
            {Object.values(groupedLayerCheckboxes).map(group => (
              <RegularCheckboxWrapper key={group.id}>
                <LayerCheckboxGroup group={group} />
              </RegularCheckboxWrapper>
            ))}

            {/* WMS / WMTS layers */}
            {shouldRenderGISControls && <GISLayerControls filter="wms-wmts" />}
          </AccordionContent>
          <ControlsBottom>
            <SelectedLayersWrapper>
              <SelectedText data-name="num-selected-layers">
                {selectedCheckboxCount} Selected
              </SelectedText>
              <ClearAllText
                data-name="clear-all-layers"
                onClick={handleClearAll}
              >
                Clear All
              </ClearAllText>
            </SelectedLayersWrapper>
          </ControlsBottom>
        </>
      )}
    </Controls>
  );
};

export { MapLayersControl };
export default MapLayersControl;
